/**
 * FarmBot OS (FBOS) 默认配置文件
 *
 * 这个文件定义了 FarmBot 操作系统的所有默认配置值。
 * 不同硬件版本（Genesis vs Express）会有不同的默认值。
 */

// ==================== 类型导入 ====================
// 从 FarmBot SDK 导入配置键的类型定义
import {
  BooleanConfigKey as BooleanFbosConfigKey,  // 布尔类型配置键
  StringConfigKey as StringFbosConfigKey,    // 字符串类型配置键
  NumberConfigKey as NumberFbosConfigKey,    // 数字类型配置键
} from "farmbot/dist/resources/configs/fbos";

import { ConfigurationName, FirmwareHardware } from "farmbot";  // 配置名称和固件硬件类型
import { getModifiedClassNameSpecifyModified } from "../default_values";  // 用于判断配置是否被修改的工具函数
import { cloneDeep } from "lodash";  // 深度复制工具

// ==================== 类型定义 ====================
/**
 * 配置键的类型联合
 * 可以是布尔、字符串、数字类型的配置键，或者配置名称
 */
type Key = BooleanFbosConfigKey
  | StringFbosConfigKey
  | NumberFbosConfigKey
  | ConfigurationName;

/**
 * 配置值的类型
 * 可以是字符串、数字、布尔值或未定义
 */
type Value = string | number | boolean | undefined;

// ==================== 默认配置值定义 ====================

/**
 * FBOS 默认配置值对象
 *
 * 这个对象包含了所有 FarmBot OS 的默认配置。
 * 新设备或重置配置时会使用这些值。
 */
const DEFAULT_FBOS_CONFIG_VALUES: Record<Key, Value> = {

  // ==================== 系统标识 ====================
  id: 0,                    // 配置记录的唯一标识符（数据库ID）
  device_id: 0,             // 关联的设备ID（数据库ID）

  // ==================== 日志配置 ====================
  // 这些配置控制哪些类型的日志会被记录和显示

  firmware_input_log: false,   // 固件输入日志 - 记录从设备发送到固件的命令
                               // 设为 true 时会显示所有发送到固件的原始数据
                               // 调试硬件通信问题时很有用

  firmware_output_log: false,  // 固件输出日志 - 记录从固件返回的响应
                               // 设为 true 时会显示从固件返回的所有原始数据
                               // 帮助调试固件响应问题

  sequence_body_log: false,    // 序列体日志 - 记录序列执行的详细步骤
                               // 设为 true 时会显示每个序列步骤的执行情况
                               // 用于追踪序列执行过程

  sequence_complete_log: false,// 序列完成日志 - 记录序列执行完成的事件
                               // 设为 true 时会显示序列执行完成的通知
                               // 用于确认序列正常完成

  sequence_init_log: false,   // 序列初始化日志 - 记录序列开始执行的事件
                               // 设为 true 时会显示序列启动的通知
                               // 用于调试序列启动问题

  // ==================== 自动更新配置 ====================
  // 控制 FarmBot 系统和固件的自动更新行为

  os_auto_update: true,       // FarmBot OS 自动更新
                              // true: 系统会自动下载并安装 OS 更新
                              // false: 需要手动触发更新
                              // 建议保持开启以获得安全修复和新功能

  fw_auto_update: true,        // 固件自动更新
                              // true: 系统会自动下载并刷写固件更新
                              // false: 需要手动更新固件
                              // 建议保持开启以获得硬件性能改进

  // ==================== 调试配置 ====================
  // 控制调试信息的输出级别

  arduino_debug_messages: false, // Arduino 调试消息
                                 // true: 显示来自 Arduino/Farmduino 的详细调试信息
                                 // 用于开发阶段或硬件故障诊断
                                 // 会产生大量日志，生产环境建议关闭

  firmware_debug_log: false,    // 固件调试日志
                               // true: 启用固件级别的详细调试日志
                               // 用于深入分析固件执行流程
                               // 会显著增加日志量

  // ==================== 固件硬件配置 ====================
  // 定义 FarmBot 使用的硬件类型和通信路径

  firmware_hardware: "arduino", // 固件硬件类型（默认 Genesis v1.2）
                               // "arduino": Genesis v1.2 (Arduino/RAMPS)
                               // "farmduino": Genesis v1.3 (Farmduino)
                               // "farmduino_k14": Genesis v1.4 (Farmduino v14)
                               // "farmduino_k15": Genesis v1.5 (Farmduino v15)
                               // "farmduino_k16": Genesis v1.6 (Farmduino v16)
                               // "farmduino_k17": Genesis v1.7 (Farmduino v17)
                               // "farmduino_k18": Genesis v1.8 (Farmduino v18)
                               // "express_k10": Express v1.0
                               // "express_k11": Express v1.1
                               // "express_k12": Express v1.2
                               // "none": 无硬件

  firmware_path: "ttyUSB0",     // 固件通信路径（默认 ttyUSB0）
                               // "ttyACM0": 推荐用于 Genesis 系列
                               // "ttyAMA0": 推荐用于 Express v1.0
                               // "ttyUSB0": 推荐用于 Express v1.1+
                               // 也可以是其他自定义路径如 "/dev/ttyUSB1"

  update_channel: "stable",    // 更新通道
                              // "stable": 稳定版更新（推荐）
                              // "beta": 测试版更新（包含新功能但可能有bug）
                              // "alpha": 内部测试版（不建议使用）
                              // 选择 stable 可以获得最稳定的体验

  // ==================== 序列配置 ====================
  // 控制序列执行的默认行为

  boot_sequence_id: undefined, // 启动序列ID
                               // undefined: 不执行启动序列
                               // 数字: 启动时自动执行指定ID的序列
                               // 用于系统启动时执行初始化任务
                               // 例如：归位、初始化传感器等

  // ==================== 高度和空间配置 ====================
  // 定义 FarmBot 的高度和安全参数

  safe_height: 0,             // 安全高度（单位：毫米）
                             // 机器人在移动到新位置时会先移动到此高度
                             // 用于避免在移动过程中碰撞植物或障碍物
                             // 0 表示不启用安全高度移动
                             // 建议设置为 50-100mm 以确保安全

  soil_height: -500,          // 土壤高度（单位：毫米）
                             // 定义农场土壤表面的Z轴坐标
                             // 负值表示在基准平面以下
                             // -500mm 通常表示土壤在基准平面以下50厘米
                             // 用于计算植物的种植深度和工具的运动范围

  gantry_height: 120,        // 门架高度（单位：毫米）
                             // 门架（X轴滑轨）相对于基准平面的高度
                             // Genesis 系列: 默认 120mm
                             // Express 系列: 默认 140mm（因为支架更高）
                             // 用于计算Z轴的运动范围和工具位置

  // ==================== 轴运动配置 ====================
  // 控制多轴同时运动的默认行为

  default_axis_order: "xy,z;high",  // 默认轴运动顺序
                                    // 格式: "轴1,轴2;高度"
                                    // "xy,z;high" 表示：X和Y轴同时移动，到达后Z轴从当前高度移至目标
                                    // 其他可能的值：
                                    //   "x,y,z;high": X→Y→Z 依次移动，都从安全高度开始
                                    //   "x,y,z;default": X→Y→Z 依次移动，从当前高度开始
                                    // 影响序列中 "Move To" 命令的执行方式
};

// ==================== Express 系列专用配置 ====================
// Express 系列硬件有不同的物理尺寸，需要调整默认值

const DEFAULT_EXPRESS_FBOS_CONFIG_VALUES =
  cloneDeep(DEFAULT_FBOS_CONFIG_VALUES);
// Express 系列的门架更高（因为使用了不同的支架结构）
DEFAULT_EXPRESS_FBOS_CONFIG_VALUES.gantry_height = 140;  // 140mm（比Genesis高20mm）

// ==================== Genesis 系列专用配置 ====================
// Genesis 系列使用标准配置值

const DEFAULT_GENESIS_FBOS_CONFIG_VALUES = DEFAULT_FBOS_CONFIG_VALUES;

// ==================== 配置获取函数 ====================

/**
 * 根据固件硬件类型获取默认配置值
 *
 * 这是一个高阶函数，接收固件硬件类型，返回一个获取配置值的函数
 *
 * @param firmwareHardware - 固件硬件类型（如 "arduino", "express_k11" 等）
 * @returns 一个函数，可以根据配置键返回对应的默认值
 *
 * 使用示例:
 *   const getVal = getDefaultConfigValue("arduino");
 *   const safeHeight = getVal("safe_height");  // 返回 0
 *   const firmwarePath = getVal("firmware_path");  // 返回 "ttyUSB0"
 */
export const getDefaultConfigValue =
  (firmwareHardware: FirmwareHardware | undefined) =>
    (key: Key): Value => {
      switch (firmwareHardware) {
        // Genesis 系列硬件（v1.2 - v1.8）
        case "arduino":
        case "farmduino":
        case "farmduino_k14":
        case "farmduino_k15":
        case "farmduino_k16":
        case "farmduino_k17":
        case "farmduino_k18":
          return DEFAULT_GENESIS_FBOS_CONFIG_VALUES[key];

        // Express 系列硬件（v1.0 - v1.2）
        case "express_k10":
        case "express_k11":
        case "express_k12":
          return DEFAULT_EXPRESS_FBOS_CONFIG_VALUES[key];

        // 其他情况（未知硬件或未设置）
        default:
          return DEFAULT_FBOS_CONFIG_VALUES[key];
      }
    };

// ==================== 配置修改检测函数 ====================

/**
 * 判断配置值是否与默认值不同
 *
 * @param key - 配置键
 * @param value - 当前配置值
 * @param firmwareHardware - 固件硬件类型
 * @returns true 表示已修改，false 表示仍为默认值
 *
 * 使用示例:
 *   const isModified = modifiedFromDefault(
 *     "safe_height",
 *     50,  // 当前值
 *     "arduino"  // 硬件类型
 *   );
 *   // 返回 true，因为默认值是 0
 */
const modifiedFromDefault = (
  key: Key,
  value: Value,
  firmwareHardware: FirmwareHardware | undefined,
) => {
  return value != getDefaultConfigValue(firmwareHardware)(key);
};

/**
 * 获取修改状态对应的CSS类名
 *
 * 用于在界面上高亮显示已修改的配置项
 * 当配置值与默认值不同时，添加 "modified" 类，使其在UI中以不同颜色显示
 *
 * @param key - 配置键
 * @param value - 当前配置值
 * @param firmwareHardware - 固件硬件类型
 * @returns CSS类名（"modified" 或 ""）
 *
 * 使用示例:
 *   <div className={getModifiedClassName("safe_height", 50, "arduino")}>
 *     Safe Height: 50mm  // 会显示为高亮（已修改）
 *   </div>
 */
export const getModifiedClassName = (
  key: Key,
  value: Value,
  firmwareHardware: FirmwareHardware | undefined,
) => {
  return getModifiedClassNameSpecifyModified(
    modifiedFromDefault(key, value, firmwareHardware));
};

// ==================== 配置分类总结 ====================

/**
 * 配置分类:
 *
 * 1. 📊 日志配置 (5项)
 *    - firmware_input_log, firmware_output_log
 *    - sequence_body_log, sequence_complete_log, sequence_init_log
 *
 * 2. 🔄 自动更新 (2项)
 *    - os_auto_update, fw_auto_update
 *
 * 3. 🔍 调试配置 (2项)
 *    - arduino_debug_messages, firmware_debug_log
 *
 * 4. 🔧 硬件配置 (3项)
 *    - firmware_hardware, firmware_path, update_channel
 *
 * 5. 🎯 序列配置 (1项)
 *    - boot_sequence_id
 *
 * 6. 📏 高度配置 (3项)
 *    - safe_height, soil_height, gantry_height
 *
 * 7. 🌐 轴运动配置 (1项)
 *    - default_axis_order
 *
 * 总计: 17 个可配置项（不含 id 和 device_id）
 */
